import classNames from 'classnames'
import { useRef } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'

import salt_48 from '@/assets/salt_48.png'
import Icon from '@/components/Icon/Icon'
import Scrollspy from '@/components/Scrollspy/Scrollspy'
import { useTheme } from '@/components/ThemeProvider/ThemeProvider'
import { groupBy, sortBy } from '@/store/cardlists/actions'
import { useAppSelector } from '@/store/hooks'
import { CardListType } from '@/types/page'
import { groupByModes, sortByModes } from '@/utils/constants'

import AdditionalFiltersButton from '../AdditionalFilters/AdditionalFiltersButton'
import JumpTo from '../JumpTo/JumpTo'

import styles from './CardListsHeader.module.scss'

export const DropdownItem = (props: any) => {
  const { activeKey, children, ...rest } = props
  const active = activeKey === rest.eventKey
  return (
    <Dropdown.Item {...rest}>
      <div className={classNames({ [styles.active]: active })}>{children}</div>
    </Dropdown.Item>
  )
}

type Props = {
  cardLists: CardListType[]
  hideControls?: boolean
  hideHeader?: boolean
  initGroupedBy?: string
  initSortedBy?: string
  sectionRefs: React.RefObject<HTMLDivElement>[]
}

const CardListsHeader = ({ cardLists, hideControls, hideHeader, initGroupedBy, initSortedBy, sectionRefs }: Props) => {
  const dropdownsRef = useRef<HTMLDivElement>(null)
  const [theme] = useTheme()
  const { groupedBy, sortedBy } = useAppSelector((state) => state.cardlists)

  return (
    <>
      {cardLists.length === 0 ? (
        <JumpTo hideHeader={hideHeader} />
      ) : (
        <Scrollspy offset={-100} sectionRefs={sectionRefs}>
          {({ currentElementIndexInViewport }) => (
            <JumpTo
              cardLists={cardLists}
              currentElementIndexInViewport={currentElementIndexInViewport}
              hideHeader={hideHeader}
            />
          )}
        </Scrollspy>
      )}
      {hideControls || (
        <div className={classNames(styles.dropdowns, 'shadow-sm')} ref={dropdownsRef}>
          <div className={styles.dropdown}>
            <label className={styles.label}>
              <Icon icon='clone' />
              {' Group by'}
            </label>
            <DropdownButton
              onSelect={(eventKey) => groupBy(eventKey as string)}
              size='sm'
              title={
                groupByModes.find(({ key }) => key === groupedBy)?.value ||
                groupByModes.find(({ key }) => key === initGroupedBy)?.value ||
                'Category'
              }
              variant={theme}
            >
              {groupByModes.map(({ icon, key, value }) => (
                <DropdownItem activeKey={groupedBy} className={styles.dropdownItem} eventKey={key} key={key}>
                  <div className='d-flex'>
                    <div className={styles.icon}>{icon && <Icon icon={icon} />}</div>
                    <span>{value}</span>
                  </div>
                </DropdownItem>
              ))}
            </DropdownButton>
          </div>
          <div className={styles.dropdown}>
            <label className={styles.label}>
              <Icon icon='arrowDownWideShort' />
              {' Sort by'}
            </label>
            <DropdownButton
              onSelect={(eventKey) => sortBy(eventKey as string)}
              size='sm'
              title={
                sortByModes.find(({ key }) => key === sortedBy)?.value ||
                sortByModes.find(({ key }) => key === initSortedBy)?.value ||
                'Default'
              }
              variant={theme}
            >
              {sortByModes.map(({ icon, key, value }) => (
                <DropdownItem activeKey={sortedBy} className={styles.dropdownItem} eventKey={key} key={key}>
                  <div className='d-flex'>
                    <div className={styles.icon}>
                      {key === 'salt' ? (
                        <img className={styles.salt} alt='Salt Score' src={salt_48.src} />
                      ) : (
                        icon && <Icon icon={icon} />
                      )}
                    </div>
                    <span>{value}</span>
                  </div>
                </DropdownItem>
              ))}
            </DropdownButton>
          </div>
          <div className={styles.dropdown}>
            <label className={styles.label} />
            <AdditionalFiltersButton />
          </div>
        </div>
      )}
    </>
  )
}

export default CardListsHeader
